import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productsAPI, settingsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { LogOut, Package, Settings, Plus, Pencil, Trash2, Eye } from 'lucide-react';
import { getFileUrl } from '../utils/fileUtils';

const AdminDashboard = () => {
  const { logout, username } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsData, settingsData] = await Promise.all([
        productsAPI.getAll(),
        settingsAPI.get(),
      ]);
      setProducts(productsData);
      setSettings(settingsData);
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Sesión cerrada');
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await productsAPI.delete(id);
      toast.success('Producto eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-xl text-stone-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-stone-800">Panel de Administración</h1>
              <p className="text-sm text-stone-600">Bienvenido, {username}</p>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline" className="border-stone-300">
                  <Eye className="mr-2 h-4 w-4" />
                  Ver Sitio
                </Button>
              </Link>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-stone-300 hover:border-red-500 hover:text-red-500"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex space-x-4 mb-6">
          <Button
            onClick={() => setActiveTab('products')}
            variant={activeTab === 'products' ? 'default' : 'outline'}
            className={activeTab === 'products' ? 'bg-clay-primary hover:bg-clay-dark' : ''}
          >
            <Package className="mr-2 h-4 w-4" />
            Productos ({products.length})
          </Button>
          <Button
            onClick={() => setActiveTab('settings')}
            variant={activeTab === 'settings' ? 'default' : 'outline'}
            className={activeTab === 'settings' ? 'bg-clay-primary hover:bg-clay-dark' : ''}
          >
            <Settings className="mr-2 h-4 w-4" />
            Configuración
          </Button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-stone-800">Gestión de Productos</h2>
              <Link to="/admin/products/new">
                <Button className="bg-clay-primary hover:bg-clay-dark">
                  <Plus className="mr-2 h-4 w-4" />
                  Nuevo Producto
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={getFileUrl(product.image)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{product.name}</CardTitle>
                    <CardDescription className="line-clamp-2">{product.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex space-x-2">
                      <Link to={`/admin/products/edit/${product.id}`} className="flex-1">
                        <Button variant="outline" className="w-full">
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        className="hover:bg-red-50 hover:border-red-500 hover:text-red-500"
                        onClick={() => handleDeleteProduct(product.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {products.length === 0 && (
              <Card className="p-12">
                <div className="text-center text-stone-600">
                  <Package className="mx-auto h-12 w-12 mb-4 text-stone-400" />
                  <p className="text-lg">No hay productos registrados</p>
                  <p className="text-sm mt-2">Comienza agregando tu primer producto</p>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <h2 className="text-2xl font-bold text-stone-800 mb-6">Configuración del Sitio</h2>
            <Link to="/admin/settings/edit">
              <Button className="bg-clay-primary hover:bg-clay-dark mb-6">
                <Settings className="mr-2 h-4 w-4" />
                Editar Configuración
              </Button>
            </Link>

            <Card>
              <CardHeader>
                <CardTitle>Información Actual</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <strong className="text-stone-700">Nombre de la Empresa:</strong>
                  <p className="text-stone-600">{settings?.companyName}</p>
                </div>
                <div>
                  <strong className="text-stone-700">Teléfono:</strong>
                  <p className="text-stone-600">{settings?.phone}</p>
                </div>
                <div>
                  <strong className="text-stone-700">Email:</strong>
                  <p className="text-stone-600">{settings?.email}</p>
                </div>
                <div>
                  <strong className="text-stone-700">Instagram:</strong>
                  <p className="text-stone-600">{settings?.instagram}</p>
                </div>
                <div>
                  <strong className="text-stone-700">Facebook:</strong>
                  <p className="text-stone-600">{settings?.facebook}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
